import { action, computed, makeObservable, observable } from 'mobx';

import { addApiCallInterceptor } from '../../services/api';
import { getCookie, getCookieDomain, removeSecureCookie, setSecureCookie } from '../../utils/secureCookies';

import { DEBUG_ENV_COOKIE, GEO_HEADER, LS_GEO, LS_INDICATOR, PR_COOKIES, SLUG_REGEX } from './constants';

const DEV_BUILD_MODES = ['local-dev', 'development'];

// Resolve the cookie `domain` for a WRITE, or `null` to signal the write must be
// SKIPPED (fail-closed). Debug cookies (x-debug env + PR deploys) are only written on a
// *.dev host, scoped to the dev subdomain (.dev.<registrable>, e.g.
// .dev.crowncoinscasino.com) so they route on every *.dev host but never leak to
// production — mirrors the sun-cli debug extension's cookie scoping. On any other host
// we refuse to fall back to the registrable domain (which production would receive).
function resolveWriteDomain(): string | null {
  const parts = window.location.hostname.split('.');
  const devIndex = parts.indexOf('dev');
  if (devIndex !== -1 && parts.length - devIndex >= 3) {
    return `.${parts.slice(devIndex).join('.')}`;
  }
  return null;
}

// Every domain a debug cookie may have been written to across this branch's history —
// cleared together because js-cookie only deletes an exact (name, path, domain) match.
// Covers the current dev scope, the registrable domain earlier commits used
// (via getCookieDomain), and the bare host (undefined).
function getClearDomains(): (string | undefined)[] {
  return [...new Set<string | undefined>([undefined, getCookieDomain(), resolveWriteDomain() ?? undefined])];
}

// Dev-only tool: must never be active in production builds (fail-closed).
export class DebugEnvStore {
  @observable private _env: string | null = null;
  @observable private _geoCountry: string = '';
  @observable private _indicatorEnabled: boolean = true;
  @observable private _prValues: Record<string, string | null> = {};

  constructor() {
    makeObservable(this);
    if (!this.isEnabled) {
      return;
    }
    this._env = getCookie(DEBUG_ENV_COOKIE) || null;
    this._geoCountry = localStorage.getItem(LS_GEO) ?? '';
    this._indicatorEnabled = localStorage.getItem(LS_INDICATOR) !== 'false';
    for (const { name } of PR_COOKIES) {
      this._prValues[name] = getCookie(name) ?? null;
    }

    // Add cf-ipcountry to the app's own API calls. Reads live state per request.
    // (The env needs no interceptor — the x-debug cookie rides along automatically.)
    addApiCallInterceptor((config) => {
      const headers = this.getGeoHeaders();
      for (const [key, value] of Object.entries(headers)) {
        config.headers.set(key, value);
      }
      return config;
    });
  }

  @computed
  public get isEnabled(): boolean {
    return DEV_BUILD_MODES.includes(import.meta.env.MODE);
  }

  @computed
  public get env(): string | null {
    return this.isEnabled ? this._env : null;
  }

  @computed
  public get geoCountry(): string {
    return this.isEnabled ? this._geoCountry : '';
  }

  @computed
  public get indicatorEnabled(): boolean {
    return this.isEnabled ? this._indicatorEnabled : false;
  }

  // Env rides as the x-debug cookie, scoped to the parent dev domain so every surface
  // (the app's own API calls, the game-ui iframe and its API calls) picks it up
  // automatically — same mechanism as the mobile app and the sun-cli debug extension.
  // The cookie is the source of truth: it's read back on init, so no localStorage copy.
  @action
  public setEnv(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) {
      this.clearEnv();
      return;
    }
    // Fail-closed: never write routing cookies when disabled (e.g. production build).
    if (!this.isEnabled) {
      return;
    }
    // Fail-closed: refuse to write a routing cookie on a non-dev host.
    const domain = resolveWriteDomain();
    if (!domain) {
      return;
    }
    this._env = trimmed;
    // 30-day TTL like the PR deploy cookies so the env survives browser restarts.
    setSecureCookie(DEBUG_ENV_COOKIE, trimmed, { domain, expires: 30 });
  }

  @action
  public clearEnv(): void {
    if (!this.isEnabled) {
      return;
    }
    this._env = null;
    // Clear every scope this branch may have written to (incl. legacy .<registrable>).
    for (const domain of getClearDomains()) {
      removeSecureCookie(DEBUG_ENV_COOKIE, { domain });
    }
  }

  @action
  public setGeo(country: string): void {
    if (!this.isEnabled) {
      return;
    }
    this._geoCountry = country;
    localStorage.setItem(LS_GEO, country);
  }

  @action
  public toggleIndicator(): void {
    if (!this.isEnabled) {
      return;
    }
    this._indicatorEnabled = !this._indicatorEnabled;
    localStorage.setItem(LS_INDICATOR, String(this._indicatorEnabled));
  }

  // Spread onto the app's own API requests. Empty when disabled/unset.
  public getGeoHeaders(): Record<string, string> {
    if (!this.isEnabled || !this._geoCountry) {
      return {};
    }
    return { [GEO_HEADER]: this._geoCountry };
  }

  @computed
  public get prValues(): Record<string, string | null> {
    return this._prValues;
  }

  public isValidSlug(value: string): boolean {
    return SLUG_REGEX.test(value);
  }

  @action
  public applyPrCookie(name: string, value: string): { ok: boolean; error?: string } {
    // Fail-closed: never write routing cookies when disabled (e.g. production build).
    if (!this.isEnabled) {
      return { ok: false };
    }
    const trimmed = value.trim();
    const config = PR_COOKIES.find((c) => c.name === name);
    if (!config) {
      return { ok: false, error: `Unknown PR cookie: ${name}` };
    }
    if (!this.isValidSlug(trimmed)) {
      return { ok: false, error: `Invalid slug "${trimmed}" — must match [a-z][a-z0-9]{3,30}` };
    }
    // Fail-closed: refuse to write a routing cookie on a non-dev host.
    const domain = resolveWriteDomain();
    if (!domain) {
      return { ok: false, error: 'Refusing to write cookie: not a *.dev host' };
    }
    // Domain-scoped to .dev.<registrable> so it routes on the matching host. 30-day TTL
    // matches the extension so the cookie survives browser restarts.
    setSecureCookie(name, trimmed, { domain, expires: 30 });
    this._prValues[name] = trimmed;
    return { ok: true };
  }

  @action
  public clearPrCookie(name: string): void {
    // Fail-closed: no-op when disabled so the tool never touches cookies in production.
    if (!this.isEnabled) {
      return;
    }
    // Clear every scope this branch may have written to (incl. legacy .<registrable>).
    for (const domain of getClearDomains()) {
      removeSecureCookie(name, { domain });
    }
    this._prValues[name] = null;
  }
}
